import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { Form, Input, Button, Typography, Alert } from 'antd';
import PropTypes from 'prop-types';
import '../common.css';

const { Title, Text } = Typography;

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=DM+Sans:wght@300;400;500&display=swap');

  .signin-root {
    min-height: 100vh;
    background: #ffffff;
    display: flex;
    font-family: 'DM Sans', sans-serif;
    overflow: hidden;
    position: relative;
  }

  /* Left decorative panel */
  .signin-left {
    width: 48%;
    background: #485daa;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    padding: 60px 48px;
  }

  .signin-left::before {
    content: '';
    position: absolute;
    top: -120px;
    left: -120px;
    width: 420px;
    height: 420px;
    background: rgba(255,255,255,0.06);
    border-radius: 50%;
  }

  .signin-left::after {
    content: '';
    position: absolute;
    bottom: -80px;
    right: -100px;
    width: 320px;
    height: 320px;
    background: rgba(255,255,255,0.05);
    border-radius: 50%;
  }

  .signin-left-dot {
    position: absolute;
    top: 40px;
    right: 40px;
    width: 12px;
    height: 12px;
    background: rgba(255,255,255,0.4);
    border-radius: 50%;
  }

  .signin-left-dot2 {
    position: absolute;
    bottom: 60px;
    left: 50px;
    width: 8px;
    height: 8px;
    background: rgba(255,255,255,0.25);
    border-radius: 50%;
  }

  .signin-left-grid {
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px);
    background-size: 48px 48px;
  }

  .signin-left-content {
    position: relative;
    z-index: 2;
    text-align: left;
    width: 100%;
  }

  .signin-tagline {
    font-family: 'Sora', sans-serif;
    font-size: 36px;
    font-weight: 700;
    color: #ffffff;
    line-height: 1.25;
    margin-bottom: 20px;
    letter-spacing: -0.5px;
  }

  .signin-tagline span {
    color: rgba(255,255,255,0.45);
  }

  .signin-sub {
    font-size: 15px;
    color: rgba(255,255,255,0.65);
    line-height: 1.7;
    max-width: 320px;
    font-weight: 300;
  }

  .signin-pill-badges {
    margin-top: 48px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .signin-badge {
    display: flex;
    align-items: center;
    gap: 12px;
    background: rgba(255,255,255,0.08);
    border: 1px solid rgba(255,255,255,0.12);
    border-radius: 12px;
    padding: 12px 18px;
    backdrop-filter: blur(8px);
  }

  .signin-badge-icon {
    width: 32px;
    height: 32px;
    background: rgba(255,255,255,0.15);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .signin-badge-icon svg {
    width: 16px;
    height: 16px;
    color: white;
  }

  .signin-badge-text {
    font-size: 13px;
    color: rgba(255,255,255,0.8);
    font-weight: 400;
  }

  /* Right form panel */
  .signin-right {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 60px 48px;
    background: #ffffff;
    position: relative;
  }

  .signin-form-wrap {
    width: 100%;
    max-width: 400px;
  }

  .signin-logo-area {
    margin-bottom: 40px;
  }

  .signin-logo-area img {
    width: 150px;
    height: auto;
    object-fit: contain;
    display: block;
  }

  .signin-heading {
    font-family: 'Sora', sans-serif;
    font-size: 26px;
    font-weight: 700;
    color: #111827;
    margin: 0 0 6px 0;
    letter-spacing: -0.3px;
  }

  .signin-subheading {
    font-size: 14px;
    color: #9ca3af;
    font-weight: 400;
    margin-bottom: 36px;
    display: block;
  }

  .signin-label {
    font-size: 13px;
    font-weight: 600;
    color: #374151;
    letter-spacing: 0.2px;
  }

  .signin-input-wrap .ant-input,
  .signin-input-wrap .ant-input-password,
  .signin-input-wrap .ant-input-affix-wrapper {
    border-radius: 10px !important;
    border: 1.5px solid #e5e7eb !important;
    background: #fafafa !important;
    font-size: 14px !important;
    height: 46px !important;
    transition: all 0.2s !important;
    box-shadow: none !important;
    font-family: 'DM Sans', sans-serif !important;
  }

  .signin-input-wrap .ant-input-affix-wrapper {
    height: 46px !important;
    padding: 0 14px !important;
    align-items: center !important;
  }

  .signin-input-wrap .ant-input-affix-wrapper input {
    height: auto !important;
    border: none !important;
    background: transparent !important;
  }

  .signin-input-wrap .ant-input:focus,
  .signin-input-wrap .ant-input-affix-wrapper:focus,
  .signin-input-wrap .ant-input-affix-wrapper-focused {
    border-color: #485daa !important;
    background: #ffffff !important;
    box-shadow: 0 0 0 3px rgba(72, 93, 170, 0.1) !important;
  }

  .signin-input-wrap .ant-input:hover,
  .signin-input-wrap .ant-input-affix-wrapper:hover {
    border-color: #485daa !important;
  }

  .signin-hint-box {
    background: #f8f9ff;
    border: 1px solid #e8ebf8;
    border-radius: 10px;
    padding: 12px 16px;
    margin-bottom: 24px;
    font-size: 13px;
    color: #6b7280;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .signin-hint-icon {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    color: #485daa;
  }

  .signin-btn {
    height: 48px !important;
    border-radius: 10px !important;
    font-weight: 600 !important;
    font-size: 15px !important;
    background: #485daa !important;
    border-color: #485daa !important;
    letter-spacing: 0.2px !important;
    font-family: 'Sora', sans-serif !important;
    transition: all 0.25s ease !important;
    box-shadow: 0 4px 16px rgba(72, 93, 170, 0.3) !important;
  }

  .signin-btn:hover {
    background: #3a4d96 !important;
    border-color: #3a4d96 !important;
    box-shadow: 0 6px 20px rgba(72, 93, 170, 0.4) !important;
    transform: translateY(-1px);
  }

  .signin-btn:active {
    transform: translateY(0);
  }

  /* Responsive */
  @media (max-width: 768px) {
    .signin-left {
      display: none;
    }
    .signin-right {
      padding: 40px 24px;
    }
  }
`;

function SignIn({ setUserData }) {
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [loading, setLoading] = useState(false);

  const { signIn } = useAuth();
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;

  const handleSignIn = async (values) => {
    setEmailError('');
    setPasswordError('');
    setLoading(true);

    try {
      const { email, password } = values;
      const deviceName = navigator.userAgent;

      const response = await fetch(`${API_URL}/auth/signin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, deviceName }),
      });

      if (response.ok) {
        const responseData = await response.json();
        const { accessToken, refreshToken, userId, role, isTempPassword } = responseData;

        localStorage.setItem('token', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        localStorage.setItem('userId', userId);
        localStorage.setItem('role', role);
        signIn(accessToken);
        setUserData({ userId, role });

        if (isTempPassword) {
          navigate('/change-password');
        } else {
          navigate('/dashboard');
        }
      } else {
        const errorResponse = await response.json();
        switch (errorResponse.error) {
          case 'UserNotFound':
            setEmailError('The email address is not registered.');
            break;
          case 'InvalidPassword':
            setPasswordError('The password is incorrect.');
            break;
          default:
            setEmailError('Email or password is incorrect.');
        }
      }
    } catch (error) {
      console.error('Error:', error);
      setEmailError('Email or password is incorrect.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{styles}</style>
      <div className="signin-root">

        {/* Left Decorative Panel */}
        <div className="signin-left">
          <div className="signin-left-grid" />
          <div className="signin-left-dot" />
          <div className="signin-left-dot2" />

          <div className="signin-left-content">
            <div className="signin-tagline">
              Manage smarter,<br />
              <span>teach</span> better.
            </div>
            <p className="signin-sub">
              Your all-in-one platform for school administration, student tracking, and academic excellence.
            </p>

            <div className="signin-pill-badges">
              <div className="signin-badge">
                <div className="signin-badge-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                </div>
                <span className="signin-badge-text">Student & Teacher Management</span>
              </div>
              <div className="signin-badge">
                <div className="signin-badge-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                    <line x1="16" y1="2" x2="16" y2="6"/>
                    <line x1="8" y1="2" x2="8" y2="6"/>
                    <line x1="3" y1="10" x2="21" y2="10"/>
                  </svg>
                </div>
                <span className="signin-badge-text">Class Scheduling & Attendance</span>
              </div>
              <div className="signin-badge">
                <div className="signin-badge-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="1" x2="12" y2="23"/>
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                  </svg>
                </div>
                <span className="signin-badge-text">Billing & Payment Tracking</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Form Panel */}
        <div className="signin-right">
          <div className="signin-form-wrap">

            {/* Logo — outside circle, 150px wide */}
            <div className="signin-logo-area">
              <img
                src="/images/plann_logo.png"
                alt="Logo"
              />
            </div>

            <h2 className="signin-heading">Welcome back</h2>
            <span className="signin-subheading">Sign in to your account to continue</span>

            <Form layout="vertical" onFinish={handleSignIn} requiredMark={false} className="signin-input-wrap">
              <Form.Item
                name="email"
                label={<span className="signin-label">Email Address</span>}
                rules={[
                  { required: true, message: 'Please input your email!' },
                  { type: 'email', message: 'Enter a valid email address.' }
                ]}
                style={{ marginBottom: 20 }}
              >
                <Input
                  size="large"
                  placeholder="you@example.com"
                  prefix={
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" style={{ marginRight: 4 }}>
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                      <polyline points="22,6 12,13 2,6"/>
                    </svg>
                  }
                />
              </Form.Item>

              <Form.Item
                name="password"
                label={<span className="signin-label">Password</span>}
                rules={[{ required: true, message: 'Please input your password!' }]}
                style={{ marginBottom: 20 }}
              >
                <Input.Password
                  size="large"
                  placeholder="Enter your password"
                  prefix={
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" style={{ marginRight: 4 }}>
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    </svg>
                  }
                />
              </Form.Item>

              {emailError && (
                <Alert
                  message={emailError}
                  type="error"
                  showIcon
                  style={{ marginBottom: 16, borderRadius: 8 }}
                />
              )}
              {passwordError && (
                <Alert
                  message={passwordError}
                  type="error"
                  showIcon
                  style={{ marginBottom: 16, borderRadius: 8 }}
                />
              )}

              <div className="signin-hint-box">
                <svg className="signin-hint-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                Forgot your credentials? Contact your school administrator for support.
              </div>

              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  block
                  className="signin-btn"
                >
                  Sign In
                </Button>
              </Form.Item>
            </Form>
          </div>
        </div>

      </div>
    </>
  );
}

SignIn.propTypes = {
  setUserData: PropTypes.func.isRequired,
};

export default SignIn;